
import React, { useState, useEffect, useCallback } from 'react';
import { Grid } from '@material-ui/core';
import { VariableSizeList as List } from 'react-window';
import { withRouter, useRouteMatch } from 'react-router-dom';
import { setFilter, getDocuments, getBundles, getSignerDocuments } from '../../../actions';
import { connect } from 'react-redux';
import { useInfiniteScroll } from '../../../customHooks/useInfiniteScroll';
import DocumentSkeleton from './DocumentSkeleton';
import MessageCard from '../MessageCard';
import { useTranslation } from 'react-i18next';
import { CONSTANTS } from '../../../constants/constants';

import DocumentCard from '../../Document/DocumentList/DocumentCard';
import DocumentCardSigner from '../../Signer/DocumentList/DocumentCard';
import NotificationCard from '../../Notification/NotificationList/NotificationCard';
import BundleCard from '../../Bundle/BundleList/BundleCard';
import FileCard from '../../File/FileList/FileCard';

const InfiniteScrollList = (props) => {
  const { documents, appConfig } = props;
  const match = useRouteMatch();
  const { path } = match;
  const { t } = useTranslation();
  const [loadingBottom, setLoadingBottom] = useState(false);
  const [listHeight, setListHeight] = useState(window.innerHeight);
  const [listWidth, setListWidth] = useState(Math.min(window.innerWidth, 1986));
  const [loadingData, noMoreData, loadNextBatchItems] = useInfiniteScroll({ ...props, path });

  const loadMoreBottom = useCallback(async () => {
    if (loadingData) return;
    setLoadingBottom(true);
    await loadNextBatchItems();
    setLoadingBottom(false);
  }, [loadingData, loadNextBatchItems]);

  const handleScroll = useCallback(({ scrollOffset, scrollUpdateWasRequested }) => {
    if (!scrollUpdateWasRequested) {
      const threshold = 100;
      const totalContentHeight = documents.length * 130;
      const isScrollable = totalContentHeight > listHeight;
      const isAtBottom = scrollOffset > totalContentHeight - listHeight - threshold;

      if (isScrollable && isAtBottom && !noMoreData && !loadingBottom) {
        loadMoreBottom();
      }
    }
  }, [documents, loadMoreBottom, loadingBottom, noMoreData, listHeight]);

  const chooseItemComponentToReturn = useCallback((item) => {
    switch (path) {
      case '/notification':
        return (
          <NotificationCard document={item} />
        );
      case '/document':
        return (
          <DocumentCard document={item} enableTags={appConfig && appConfig.enableTags} />
        );
      case '/bundle':
        return (
          <BundleCard bundle={item} enableTags={appConfig && appConfig.enableTags} />
        );
      case '/file':
        return (
          <FileCard document={item} appConfig={appConfig && appConfig} />
        );
      case '/document/finished':
        return (
          <DocumentCard document={item} enableTags={appConfig && appConfig.enableTags} />
        );
      case '/signer/document':
        return (
          <DocumentCardSigner document={item.document} />
        );
    }
  }, [path]);
  const getItemSize = useCallback((index) => {
    const item = documents[index];
    let baseHeight = 130;

    if (item.status === CONSTANTS.DOCUMENT.STATUS.REJECTED ||
      item.status === CONSTANTS.DOCUMENT.STATUS.CANCELLED ||
      item.status === CONSTANTS.DOCUMENT.STATUS.ERROR ||
      (item.flow && item.flow.status === 'sendToSign' && item.flow.expirationDate) ||
      (item.flow && item.flow.status === 'expired')) {
      baseHeight += 30;
    }

    if (!item.fileUrl) {
      baseHeight += 20;
    }

    if (item.tags && item.tags.length > 0) {
      const tagRows = Math.ceil(item.tags.length / 2);
      baseHeight += tagRows * 20;
    }

    return baseHeight;
  }, [documents]);

  const handleResize = useCallback(() => {
    const headerHeight = 150;
    const availableHeight = window.innerHeight - headerHeight;
    const availableWidth = Math.min(window.innerWidth, 1986); // Limitar el ancho máximo a 1986px
    setListHeight(availableHeight);
    setListWidth(availableWidth);
  }, []);

  const Row = useCallback(({ index, style }) => {
    const item = documents[index];
    return (
      <div style={style} data-id={item.id}>
        {chooseItemComponentToReturn(item)}
        {loadingBottom && index === documents.length - 1 && <DocumentSkeleton />}
        {noMoreData && index === documents.length - 1 &&
          <Grid container style={{
            marginBottom: '25px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <MessageCard message={t('documentListView:noMoreData')} />
          </Grid>}
      </div>
    );
  }, [documents, loadingBottom, noMoreData, chooseItemComponentToReturn, t]);

  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);
  if (loadingData && (!loadingBottom)) {
    return (
      <Grid container spacing={2} alignItems='center'>
        {Array.from(new Array(8)).map((_, index) => (

          <DocumentSkeleton key={index} />

        ))}
      </Grid>
    );
  }

  return (

    <Grid container spacing={3}>
      {documents.length ? <List
        onScroll={handleScroll}
        className='infiniteScrollList'
        height={listHeight}
        itemCount={documents.length}
        itemSize={getItemSize}
        width={listWidth}
      >
        {Row}
      </List> : <MessageCard
        message={t('documentListView:noMoreData')}
      />
      }
    </Grid>

  );
};
const mapStateToProps = (state) => {
  return {
    filter: state.filter,
    documents: state.documents.documents,
    appConfig: state.config
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setFilter: (data) => dispatch(setFilter(data)),
    getDocuments: (currentPage, search, docType, nextToken) => dispatch(getDocuments(currentPage, search, docType, nextToken)),
    getBundles: (searchByDocument, nextToken) => dispatch(getBundles(searchByDocument, nextToken)),
    getSignerDocuments: (search, nextToken) => dispatch(getSignerDocuments(search, nextToken))
  };
};
export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(InfiniteScrollList)
);
